import usePermissionsStore from '@/stores/usePermissionsStore'
import { resources, scopes } from '@/utils/permission'
import authSdk from '@/utils/sdk'
import { jwtDecode } from 'jwt-decode'
import { useMemo } from 'react'

// Nhóm Keycloak được xem hoá đơn điện tử: phòng Kế toán (Finance), ban lãnh đạo (BOM, BOD), Admin
const E_INVOICE_GROUPS = ['finance', 'bom', 'bod', 'admin']

/**
 * Nhóm Keycloak của user (claim `groups` trong token), chuẩn hoá: lấy đoạn cuối của path, chữ
 * thường (vd `/Leadership/BOD` → `bod`). Ưu tiên token authorization (gửi lên API), không có thì
 * access token. Không đọc được → [].
 */
const getTokenGroups = (): string[] => {
    const token = authSdk.authorizationAccessToken() || authSdk.getAccessToken()
    if (!token) return []
    try {
        const { groups } = jwtDecode<{ groups?: unknown }>(token)
        if (!Array.isArray(groups)) return []
        return groups
            .filter((group): group is string => typeof group === 'string')
            .map((group) => group.split('/').filter(Boolean).pop() ?? '')
            .map((group) => group.toLowerCase())
    } catch {
        return []
    }
}

/**
 * Quyền hoá đơn điện tử (MISA meInvoice), theo FE_INTEGRATION.md mục Quyền:
 * - `canView`: thuộc nhóm finance / bom / bod / admin (hoặc super admin) → thấy khối "Hoá đơn điện
 *   tử", cột + filter "HĐ điện tử" ở danh sách. Không thuộc nhóm thì ẩn hết (backend cũng trả 403 /
 *   field rỗng).
 * - `canManage`: `canView` + quyền `invoices:approved` → thấy nút Phát hành hoá đơn, Xác nhận hoá đơn sai.
 */
export const useEInvoiceAccess = () => {
    const { permissions, isAdmin } = usePermissionsStore()

    return useMemo(() => {
        const groups = getTokenGroups()
        const canView =
            isAdmin || groups.some((group) => E_INVOICE_GROUPS.includes(group))
        const invoiceScopes =
            permissions.find(
                (permission) => permission?.rsname === resources.invoices
            )?.scopes ?? []
        const canManage =
            canView && (isAdmin || invoiceScopes.includes(scopes.approved))
        return { canView, canManage }
    }, [permissions, isAdmin])
}
